import {
  Component,
  ContentChild,
  DoCheck,
  HostBinding,
  Input,
  Optional,
} from '@angular/core';
import {
  FormGroup,
  FormGroupDirective,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { FormControlChildAbstract } from '~/component/form/abstracts/control-child.abstract';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { ToggleDefaultValueFormFieldInterface } from '~/toggles/default-values/interfaces/form-field.interface';
import { ToggleDefaultValueRuleInterface } from '~/toggles/default-values/interfaces/rule.interface';

@Component({
  selector: 'app-form-control',
  templateUrl: './control.component.html',
  styleUrls: ['./control.component.scss'],
})
export class FormControlComponent implements DoCheck {
  @ContentChild('controlChild')
  public contentChild: FormControlChildAbstract<string[]>;

  @Input() public currentCharactersCounter?: number;
  @Input() public description: string;
  @Input() public errorMessages: string[] = [];
  @Input() public formControlNameToggle?: string;
  @Input() public isExternalLabel = false;
  @Input() public isHorizontal = false;
  @Input() public label: string;
  @Input() public title: string;
  @Input() public toggle?: ToggleDefaultValueFormFieldInterface;
  @Input() public totalCharactersCounter?: number;

  private readonly validatorsNameToFunction: {
    [key: string]: (...args) => ValidatorFn;
  } = {
    pattern: Validators.pattern,
  };

  public errorIconName = VtmnPlayIconTypeEnum.WarningError;

  constructor(
    @Optional() private readonly formGroupDirective: FormGroupDirective
  ) {}

  public ngDoCheck(): void {
    if (!this.toggle || !this.formControlNameToggle || !this.formGroupToggle) {
      return;
    }

    const formControl = this.formGroupToggle.get(this.formControlNameToggle);

    if (!this.shown) {
      formControl.clearValidators();
      formControl.updateValueAndValidity();
      return;
    }

    const validators: ValidatorFn[] = [];

    // Validators
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.toggle.validators) {
      validators.push(
        ...this.toggle.validators.map((validator) =>
          this.validatorsNameToFunction[validator.type](validator.value)
        )
      );
    }

    if (validators.length) {
      formControl.setValidators(validators);
    } else {
      formControl.clearValidators();
    }

    // State
    if (this.disabled) {
      formControl.disable();
    } else {
      formControl.enable();
    }

    // Update
    formControl.updateValueAndValidity();
  }

  @HostBinding('style.display')
  public get htmlShown(): string {
    return this.shown ? '' : 'none';
  }

  private get formGroupToggle(): FormGroup {
    return this.formGroupDirective?.form;
  }

  public get disabled(): boolean {
    return this.toggle?.disabled !== undefined
      ? this.evaluateCondition(this.toggle.disabled)
      : !!this.contentChild?.disabled;
  }

  public get hasError(): boolean {
    return !!this.contentChild?.hasError;
  }

  public get labelDisplayed(): string {
    if (!this.label) {
      return '';
    }

    return this.required ? `${this.label}*` : this.label;
  }

  public get required(): boolean {
    if (this.toggle?.required !== undefined) {
      return this.evaluateCondition(this.toggle.required);
    }
    return this.toggle?.requiredOr !== undefined
      ? this.evaluateOrCondition(this.toggle.requiredOr)
      : !!this.contentChild?.required;
  }

  public get shown(): boolean {
    return this.toggle?.shown !== undefined
      ? this.evaluateCondition(this.toggle.shown)
      : this.toggle?.shownOr !== undefined
        ? this.evaluateOrCondition(this.toggle.shownOr)
        : true;
  }

  private evaluateCondition(
    rules: boolean | ToggleDefaultValueRuleInterface[]
  ): boolean {
    if (!this.formGroupToggle) {
      return true;
    }
    if (typeof rules === 'boolean') {
      return rules;
    }

    for (const { key, operator, value } of rules) {
      const keyValue = this.formGroupToggle.value[key];

      if (
        (operator === '=' && keyValue !== value) ||
        (operator === '!=' && keyValue === value)
      ) {
        return false;
      }
    }

    return true;
  }

  private evaluateOrCondition(
    rules: ToggleDefaultValueRuleInterface[]
  ): boolean {
    if (!this.formGroupToggle) {
      return true;
    }

    for (const { key, operator, value } of rules) {
      const keyValue = this.formGroupToggle.value[key];
      if (
        (operator === '=' && keyValue === value) ||
        (operator === '!=' && keyValue !== value)
      ) {
        return true;
      }
    }
    return false;
  }
}
